import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import { extractErrorMessage } from '../Services/httpClient';
import { getTestTargets, sendTest } from '../Services/CatalogApi';
import { describeTestResult, TEST_CHANNEL_LABELS, testableChannels, testBlockedReason, testDestinations } from '../Services/catalogModel';

const ORANGE = '#f77513';

/**
 * Envio de teste do rascunho (C6). Escolhe-se o canal e um destino da lista permitida, que vem da configuração do serviço: ninguém digita um
 * endereço aqui. O servidor manda o que está SALVO, composto pelo motor de verdade (o HTML que chega é o que sairia), com o assunto marcado
 * como teste. A tela só mostra o que o servidor respondeu.
 */
const TestSendDialog = ({ version, itemKey, onClose, onDone }) => {
    const channels = useMemo(() => testableChannels(version), [version]);
    const [targets, setTargets] = useState(null);
    const [channel, setChannel] = useState(channels[0] ?? 'email');
    const [destination, setDestination] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        getTestTargets().then((t) => alive && setTargets(t)).catch((err) => alive && setError(extractErrorMessage(err)));
        return () => { alive = false; };
    }, []);

    const options = useMemo(() => testDestinations(targets, channel), [targets, channel]);
    useEffect(() => { setDestination(options[0]?.value ?? ''); }, [options]);

    const blocked = targets ? testBlockedReason(targets, channel) : null;

    const handleSend = async () => {
        setBusy(true);
        setError(null);
        setResult(null);
        try {
            setResult(describeTestResult(await sendTest(version.id, channel, destination), channel));
            onDone?.();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle>Enviar teste de {itemKey} (v{version.number})</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Manda o rascunho salvo só a um destino da lista de teste, com o assunto marcado como [TESTE]. É o e-mail como o motor o monta, não a pré-visualização.
                </Typography>
                {!targets && !error && <CircularProgress size={22} style={{ color: ORANGE }} />}
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                {result && <Alert severity={result.severity} sx={{ mb: 2 }}>{result.message}</Alert>}
                {targets && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField select size="small" label="Canal" value={channel} onChange={(e) => { setChannel(e.target.value); setResult(null); setError(null); }}>
                            {channels.map((c) => <MenuItem key={c} value={c}>{TEST_CHANNEL_LABELS[c]}</MenuItem>)}
                        </TextField>
                        {blocked
                            ? <Alert severity="info">{blocked}</Alert>
                            : (
                                <TextField select size="small" label="Destino" value={destination} onChange={(e) => { setDestination(e.target.value); setResult(null); setError(null); }}>
                                    {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                </TextField>
                            )}
                        {channel === 'push' && !blocked && (
                            <Typography variant="caption" color="text.secondary">
                                O push vai para os aparelhos em que esse perfil ativou as notificações. Sem nenhum aparelho ativo, o teste não sai.
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            Até {targets.maxPerHour} testes por hora por pessoa. O envio fica registrado no histórico do item.
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy} style={{ color: '#777' }}>Fechar</Button>
                <Button onClick={handleSend} variant="contained" disabled={busy || !targets || !!blocked || !destination}
                    style={busy || !targets || blocked || !destination ? undefined : { backgroundColor: ORANGE, color: 'white' }}>
                    {busy ? 'Enviando...' : 'Enviar teste'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TestSendDialog;
